import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Check, Copy, Loader2, Search, ShieldCheck, UserCheck, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_protected/admin/provider-insurance')({
  component: ProviderInsurancePage,
});

interface InsuranceWithStatus {
  insuranceId: string;
  displayName: string;
  ehrId: string;
  accepts: boolean | null;
}

interface BulkChangeItem {
  insuranceId: string;
  displayName: string;
  accepts: boolean | null;
}

function ProviderInsurancePage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const router = useRouter();

  // State
  const [selectedEhrSystem, setSelectedEhrSystem] = useState<'imedicware'>('imedicware');
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'accepts' | 'rejects' | 'unknown'>(
    'all'
  );
  const [selectedInsurances, setSelectedInsurances] = useState<Set<string>>(new Set());
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    changes: BulkChangeItem[];
    action: 'accepts' | 'rejects' | 'unknown' | null;
  }>({ isOpen: false, changes: [], action: null });
  const [copyFromProviderId, setCopyFromProviderId] = useState<string>('');

  // Use current organization ID from Clerk
  const currentOrganizationId = organization?.id;

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // tRPC queries
  const providerInsuranceQuery = trpc.admin.getProviderInsuranceAcceptances.useQuery(
    { organizationId: currentOrganizationId || '', ehrSystem: selectedEhrSystem },
    { enabled: !!currentOrganizationId }
  );

  // tRPC mutations
  const updateAcceptanceMutation = trpc.admin.updateProviderInsuranceAcceptance.useMutation({
    onSuccess: () => {
      toast.success('Insurance acceptance updated successfully');
      providerInsuranceQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update insurance acceptance: ${error.message}`);
    },
  });

  const bulkUpdateMutation = trpc.admin.bulkUpdateProviderInsuranceAcceptances.useMutation({
    onSuccess: () => {
      toast.success('Bulk insurance update successful');
      providerInsuranceQuery.refetch();
      setSelectedInsurances(new Set());
      setConfirmDialog({ isOpen: false, changes: [], action: null });
    },
    onError: (error) => {
      toast.error(`Failed to bulk update: ${error.message}`);
    },
  });

  // Get provider-specific insurance data
  const currentProviderData = useMemo(() => {
    if (!selectedProviderId || !providerInsuranceQuery.data) return null;
    return providerInsuranceQuery.data.matrix.find((p) => p.provider.id === selectedProviderId);
  }, [selectedProviderId, providerInsuranceQuery.data]);

  // Filter and search insurances
  const filteredInsurances = useMemo(() => {
    if (!currentProviderData) return [];

    let filtered = currentProviderData.insurances;

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (ins) =>
          ins.displayName.toLowerCase().includes(search) || ins.ehrId.toLowerCase().includes(search)
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((ins) => {
        if (filterStatus === 'accepts') return ins.accepts === true;
        if (filterStatus === 'rejects') return ins.accepts === false;
        if (filterStatus === 'unknown') return ins.accepts === null;
        return true;
      });
    }

    return filtered;
  }, [currentProviderData, searchTerm, filterStatus]);

  // Group insurances by carrier/type (simplified grouping by first word)
  const groupedInsurances = useMemo(() => {
    const groups = new Map<string, InsuranceWithStatus[]>();

    for (const ins of filteredInsurances) {
      // Simple grouping by first significant word
      const firstWord = ins.displayName.split(/[\s-]/)[0]?.toUpperCase() || 'OTHER';
      const group = groups.get(firstWord) || [];
      group.push(ins);
      groups.set(firstWord, group);
    }

    // Sort groups by name and sort insurances within each group
    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, insurances]) => ({
        name,
        insurances: insurances.sort((a, b) => a.displayName.localeCompare(b.displayName)),
      }));
  }, [filteredInsurances]);

  const handleBulkAction = (action: 'accepts' | 'rejects' | 'unknown') => {
    if (selectedInsurances.size === 0) return;

    const changes = Array.from(selectedInsurances).map((id) => {
      const insurance = filteredInsurances.find((ins) => ins.insuranceId === id);
      return {
        insuranceId: id,
        displayName: insurance?.displayName || '',
        accepts: action === 'accepts' ? true : action === 'rejects' ? false : null,
      };
    });

    setConfirmDialog({
      isOpen: true,
      changes,
      action,
    });
  };

  const confirmBulkUpdate = () => {
    if (!selectedProviderId || !currentOrganizationId || !confirmDialog.action) return;

    const updates = confirmDialog.changes.map((change) => ({
      insuranceId: change.insuranceId,
      accepts: change.accepts,
    }));

    bulkUpdateMutation.mutate({
      organizationId: currentOrganizationId,
      providerId: selectedProviderId,
      updates,
    });
  };

  const handleCopySettings = () => {
    if (!copyFromProviderId || !selectedProviderId || !currentOrganizationId) return;

    const sourceProvider = providerInsuranceQuery.data?.matrix.find(
      (p) => p.provider.id === copyFromProviderId
    );

    if (!sourceProvider) return;

    // Copy all insurance settings from source provider
    const updates = sourceProvider.insurances.map((ins) => ({
      insuranceId: ins.insuranceId,
      accepts: ins.accepts,
    }));

    bulkUpdateMutation.mutate({
      organizationId: currentOrganizationId,
      providerId: selectedProviderId,
      updates,
    });
  };

  const handleSingleUpdate = (insuranceId: string, accepts: boolean | null) => {
    if (!selectedProviderId || !currentOrganizationId) return;

    updateAcceptanceMutation.mutate({
      organizationId: currentOrganizationId,
      providerId: selectedProviderId,
      insuranceId,
      accepts,
    });
  };

  // Stats for current provider
  const stats = useMemo(() => {
    if (!currentProviderData) return { accepts: 0, rejects: 0, unknown: 0, total: 0 };

    const accepts = currentProviderData.insurances.filter((i) => i.accepts === true).length;
    const rejects = currentProviderData.insurances.filter((i) => i.accepts === false).length;
    const unknown = currentProviderData.insurances.filter((i) => i.accepts === null).length;

    return { accepts, rejects, unknown, total: currentProviderData.insurances.length };
  }, [currentProviderData]);

  // Loading state
  if (isAdminLoading || !orgLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Access denied for non-super admins
  if (!isSuperAdmin) {
    return null;
  }

  const providers = providerInsuranceQuery.data?.providers || [];

  return (
    <AdminPageLayout
      title="Provider Insurance Acceptance"
      description="Configure which insurance plans each provider accepts"
      icon={ShieldCheck}
      isSuperAdminPage={true}
    >
      <div className="flex flex-col gap-6">
        {/* Provider Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Select Provider
            </CardTitle>
            <CardDescription>
              Choose a provider to manage their insurance acceptances
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <Select value={selectedProviderId} onValueChange={setSelectedProviderId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a provider..." />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name} (EHR: {provider.ehrId})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedProviderId && (
                <div className="flex gap-2">
                  <Select value={copyFromProviderId} onValueChange={setCopyFromProviderId}>
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Copy settings from..." />
                    </SelectTrigger>
                    <SelectContent>
                      {providers
                        .filter((p) => p.id !== selectedProviderId)
                        .map((provider) => (
                          <SelectItem key={provider.id} value={provider.id}>
                            {provider.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleCopySettings}
                    disabled={!copyFromProviderId || bulkUpdateMutation.isPending}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy All Settings
                  </Button>
                </div>
              )}
            </div>

            {/* Provider Stats */}
            {selectedProviderId && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{stats.total}</div>
                    <p className="text-xs text-muted-foreground">Total Plans</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">{stats.accepts}</div>
                    <p className="text-xs text-muted-foreground">Accepted</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-red-600">{stats.rejects}</div>
                    <p className="text-xs text-muted-foreground">Not Accepted</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-gray-600">{stats.unknown}</div>
                    <p className="text-xs text-muted-foreground">Unknown</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Insurance Management */}
        {selectedProviderId && currentProviderData && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Insurance Plans</CardTitle>
                  <CardDescription>
                    Manage acceptance status for {currentProviderData.provider.name}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search insurances..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 w-64"
                    />
                  </div>
                  <Select
                    value={filterStatus}
                    onValueChange={(v) => setFilterStatus(v as typeof filterStatus)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="accepts">Accepts</SelectItem>
                      <SelectItem value="rejects">Doesn't Accept</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Bulk Actions Bar */}
              {selectedInsurances.size > 0 && (
                <div className="mb-4 p-3 bg-muted rounded-lg flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {selectedInsurances.size} insurance(s) selected
                  </span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('accepts')}>
                      <Check className="h-4 w-4 mr-1" />
                      Mark as Accepted
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('rejects')}>
                      <X className="h-4 w-4 mr-1" />
                      Mark as Not Accepted
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction('unknown')}>
                      Mark as Unknown
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedInsurances(new Set())}
                    >
                      Clear Selection
                    </Button>
                  </div>
                </div>
              )}

              {/* Tabbed View */}
              <Tabs defaultValue="grouped" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="grouped">Grouped View</TabsTrigger>
                  <TabsTrigger value="list">List View</TabsTrigger>
                </TabsList>

                {/* Grouped View */}
                <TabsContent value="grouped" className="space-y-4">
                  {groupedInsurances.map((group) => (
                    <div key={group.name} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-sm">
                          {group.name} ({group.insurances.length})
                        </h3>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const ids = new Set(selectedInsurances);
                            for (const ins of group.insurances) {
                              if (ids.has(ins.insuranceId)) {
                                ids.delete(ins.insuranceId);
                              } else {
                                ids.add(ins.insuranceId);
                              }
                            }
                            setSelectedInsurances(ids);
                          }}
                        >
                          Toggle Group
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {group.insurances.map((insurance) => (
                          <div
                            key={insurance.insuranceId}
                            className="flex items-center justify-between p-2 border rounded-lg hover:bg-muted/50"
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <Checkbox
                                checked={selectedInsurances.has(insurance.insuranceId)}
                                onCheckedChange={(checked) => {
                                  const newSet = new Set(selectedInsurances);
                                  if (checked) {
                                    newSet.add(insurance.insuranceId);
                                  } else {
                                    newSet.delete(insurance.insuranceId);
                                  }
                                  setSelectedInsurances(newSet);
                                }}
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium">{insurance.displayName}</div>
                                <div className="text-xs text-muted-foreground">
                                  ID: {insurance.ehrId}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant={insurance.accepts === true ? 'default' : 'outline'}
                                className="h-7 w-7 p-0"
                                onClick={() => handleSingleUpdate(insurance.insuranceId, true)}
                                disabled={updateAcceptanceMutation.isPending}
                              >
                                <Check className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant={insurance.accepts === false ? 'destructive' : 'outline'}
                                className="h-7 w-7 p-0"
                                onClick={() => handleSingleUpdate(insurance.insuranceId, false)}
                                disabled={updateAcceptanceMutation.isPending}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant={insurance.accepts === null ? 'secondary' : 'outline'}
                                className="h-7 px-2 text-xs"
                                onClick={() => handleSingleUpdate(insurance.insuranceId, null)}
                                disabled={updateAcceptanceMutation.isPending}
                              >
                                ?
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </TabsContent>

                {/* List View */}
                <TabsContent value="list" className="space-y-2">
                  {filteredInsurances.map((insurance) => (
                    <div
                      key={insurance.insuranceId}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Checkbox
                          checked={selectedInsurances.has(insurance.insuranceId)}
                          onCheckedChange={(checked) => {
                            const newSet = new Set(selectedInsurances);
                            if (checked) {
                              newSet.add(insurance.insuranceId);
                            } else {
                              newSet.delete(insurance.insuranceId);
                            }
                            setSelectedInsurances(newSet);
                          }}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{insurance.displayName}</div>
                          <div className="text-sm text-muted-foreground">
                            EHR ID: {insurance.ehrId}
                          </div>
                        </div>
                        <Badge
                          variant={
                            insurance.accepts === true
                              ? 'default'
                              : insurance.accepts === false
                                ? 'destructive'
                                : 'secondary'
                          }
                        >
                          {insurance.accepts === true
                            ? 'Accepts'
                            : insurance.accepts === false
                              ? "Doesn't Accept"
                              : 'Unknown'}
                        </Badge>
                      </div>
                      <div className="flex gap-1 ml-4">
                        <Button
                          size="sm"
                          variant={insurance.accepts === true ? 'default' : 'outline'}
                          className="h-8 w-8 p-0"
                          onClick={() => handleSingleUpdate(insurance.insuranceId, true)}
                          disabled={updateAcceptanceMutation.isPending}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={insurance.accepts === false ? 'destructive' : 'outline'}
                          className="h-8 w-8 p-0"
                          onClick={() => handleSingleUpdate(insurance.insuranceId, false)}
                          disabled={updateAcceptanceMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant={insurance.accepts === null ? 'secondary' : 'outline'}
                          className="h-8 px-3 text-xs"
                          onClick={() => handleSingleUpdate(insurance.insuranceId, null)}
                          disabled={updateAcceptanceMutation.isPending}
                        >
                          ?
                        </Button>
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bulk Update Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.isOpen}
        onOpenChange={(open: boolean) =>
          !open && setConfirmDialog({ isOpen: false, changes: [], action: null })
        }
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Bulk Update</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <div>
                You are about to update {confirmDialog.changes.length} insurance plan(s) to:
                <Badge
                  className="ml-2"
                  variant={
                    confirmDialog.action === 'accepts'
                      ? 'default'
                      : confirmDialog.action === 'rejects'
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {confirmDialog.action === 'accepts'
                    ? 'Accepts'
                    : confirmDialog.action === 'rejects'
                      ? "Doesn't Accept"
                      : 'Unknown'}
                </Badge>
              </div>
              <div className="max-h-60 overflow-y-auto border rounded p-2 text-sm">
                {confirmDialog.changes.map((change) => (
                  <div key={change.insuranceId} className="py-1">
                    • {change.displayName}
                  </div>
                ))}
              </div>
              <div className="text-sm text-muted-foreground">
                This will affect insurance verification for patient appointments.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkUpdate}>
              Update {confirmDialog.changes.length} Insurance(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPageLayout>
  );
}
